import { useMemo } from "react";

/**
 * Input Action Handlers Hook
 * 
 * This hook provides a clean integration layer between the InputActionIcons component
 * and app-level business logic implementations. It allows apps to provide handlers
 * for each popup option in a granular, isolated manner.
 * 
 * ARCHITECTURE:
 * - Pure integration layer - no business logic
 * - Connects UI component to app-level handlers
 * - Allows granular implementation of each feature
 * - Maintains separation of concerns
 * 
 * USAGE:
 * ```tsx
 * // In your app component
 * const handlers = useInputActionHandlers({
 *   onRecordAudio: async () => {
 *     // Your audio recording implementation
 *   },
 *   onAttachFromDevice: async () => {
 *     // Your file attachment implementation
 *   },
 *   // ... other handlers as needed
 * });
 * 
 * // Component automatically uses these handlers
 * return <InputActionIcons isConnected={true} />;
 * ```
 */

export interface InputActionHandlers {
  // Audio options
  onRecordAudio?: () => Promise<void>;
  onStartVoiceChat?: () => Promise<void>;
  
  // Video options  
  onRecordVideo?: () => Promise<void>;
  onStartVideoChat?: () => Promise<void>;
  
  // Attachment options
  onAttachFromDevice?: () => Promise<void>;
  onAttachFromConnection?: () => Promise<void>;
  onAttachFromWorkspace?: () => Promise<void>;
  
  // Permission management (optional - for advanced use cases)
  onCheckPermissions?: () => Promise<void>;
  onRequestMicrophonePermission?: () => Promise<void>;
  onRequestCameraPermission?: () => Promise<void>;
}

export interface UseInputActionHandlersOptions {
  /**
   * Enable automatic error handling and logging
   * @default true
   */
  enableErrorHandling?: boolean;
  
  /**
   * Custom error handler for when actions fail
   */
  onError?: (error: Error, action: string) => void;
  
  /**
   * Enable debug logging for development
   * @default false
   */
  enableDebugLogging?: boolean;
  
  /**
   * Authentication state to pass to handlers
   */
  isAuthenticated?: boolean;
}

/**
 * Hook that wraps input action handlers with error handling and debug logging.
 * 
 * This hook serves as the integration point between the InputActionIcons UI component
 * and your app's business logic implementations. Each handler corresponds to a specific
 * popup option in the component.
 * 
 * @param handlers - Object containing handler functions for each action
 * @param options - Configuration options for error handling and debugging
 * @returns Wrapped handlers with error handling and optional debug logging applied
 */
export function useInputActionHandlers(
  handlers: InputActionHandlers,
  options: UseInputActionHandlersOptions = {}
): InputActionHandlers {
  const {
    enableErrorHandling = true,
    onError,
    enableDebugLogging = false,
    isAuthenticated = false,
  } = options;

  // Memoize wrapped handlers to prevent infinite re-renders
  const wrappedHandlers = useMemo(() => {
    const wrapHandler = (
      handler: (() => Promise<void>) | undefined,
      actionName: string
    ): (() => Promise<void>) | undefined => {
      if (!handler) return undefined;

      return async () => {
        try {
          if (enableDebugLogging) {
            console.log(`[InputActionHandlers] Executing: ${actionName}`, { isAuthenticated });
          }

          if (!isAuthenticated) {
            if (enableDebugLogging) {
              console.warn(`[InputActionHandlers] Logging: ${actionName}: not authenticated`);
            }
            // return;
          }

          await handler();

          if (enableDebugLogging) {
            console.log(`[InputActionHandlers] Completed: ${actionName}`);
          }
        } catch (error) {
          const errorObj = error instanceof Error ? error : new Error(String(error));
          
          if (enableDebugLogging) {
            console.error(`[InputActionHandlers] Error in ${actionName}:`, errorObj);
          }

          if (enableErrorHandling) {
            if (onError) {
              onError(errorObj, actionName);
            } else {
              // Default error handling - could be enhanced with toast notifications
              console.error(`Action failed: ${actionName}`, errorObj);
            }
          } else {
            // Re-throw if error handling is disabled
            throw errorObj;
          }
        }
      };
    };

    // Create wrapped handlers object
    return {
      onRecordAudio: wrapHandler(handlers.onRecordAudio, "recordAudio"),
      onStartVoiceChat: wrapHandler(handlers.onStartVoiceChat, "startVoiceChat"),
      onRecordVideo: wrapHandler(handlers.onRecordVideo, "recordVideo"),
      onStartVideoChat: wrapHandler(handlers.onStartVideoChat, "startVideoChat"),
      onAttachFromDevice: wrapHandler(handlers.onAttachFromDevice, "attachFromDevice"),
      onAttachFromConnection: wrapHandler(handlers.onAttachFromConnection, "attachFromConnection"),
      onAttachFromWorkspace: wrapHandler(handlers.onAttachFromWorkspace, "attachFromWorkspace"),
      onCheckPermissions: wrapHandler(handlers.onCheckPermissions, "checkPermissions"),
      onRequestMicrophonePermission: wrapHandler(handlers.onRequestMicrophonePermission, "requestMicrophonePermission"),
      onRequestCameraPermission: wrapHandler(handlers.onRequestCameraPermission, "requestCameraPermission"),
    };
  }, [
    // Dependencies for memoization
    handlers.onRecordAudio,
    handlers.onStartVoiceChat,
    handlers.onRecordVideo,
    handlers.onStartVideoChat,
    handlers.onAttachFromDevice,
    handlers.onAttachFromConnection,
    handlers.onAttachFromWorkspace,
    handlers.onCheckPermissions,
    handlers.onRequestMicrophonePermission,
    handlers.onRequestCameraPermission,
    enableErrorHandling,
    onError,
    enableDebugLogging,
    isAuthenticated,
  ]);

  return wrappedHandlers;
}

/**
 * Convenience hook for apps that only need a subset of handlers.
 * Provides better TypeScript inference for partial implementations.
 */
export function usePartialInputActionHandlers(
  handlers: Partial<InputActionHandlers>,
  options?: UseInputActionHandlersOptions
): Partial<InputActionHandlers> {
  return useInputActionHandlers(handlers as InputActionHandlers, options);
}

/**
 * Type guard to check if a handler is implemented
 */
export function isHandlerImplemented<K extends keyof InputActionHandlers>(
  handlers: InputActionHandlers,
  handlerKey: K
): handlers is InputActionHandlers & Required<Pick<InputActionHandlers, K>> {
  return typeof handlers[handlerKey] === "function";
}

/**
 * Utility to create a no-op handler (useful for testing or placeholder implementations)
 */
export function createNoOpHandler(message?: string): () => Promise<void> {
  return async () => {
    if (message) {
      console.log(`[NoOp Handler] ${message}`);
    }
  };
}

/**
 * Utility to create a handler that shows a "not implemented" message
 */
export function createNotImplementedHandler(featureName: string): () => Promise<void> {
  return async () => {
    console.warn(`[Not Implemented] ${featureName} is not yet implemented`);
    // Could be enhanced to show a toast notification or modal
  };
}
